<script lang="ts" setup>
import {
    faAddressCard,
    faArrowsRotate,
    faBadgeCheck,
    faBolt,
    faCalendarPen,
    faClock,
    faClockRotateLeft,
    faEnvelopes,
    faFilePlus,
    faFilters,
    faGlobePointer,
    faListCheck,
    faMagnifyingGlass,
    faMessages,
    faPenToSquare,
    faSparkles,
    faSquarePollVertical,
} from "@fortawesome/pro-duotone-svg-icons";
import { faChevronRight } from "@fortawesome/pro-light-svg-icons";
import {
    faBars,
    faCheck,
    faHome,
    type IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import type {
    AutomationTemplateMetadataStep,
    Template,
} from "~/models/Template";
import type { TemplateObject } from "~/components/automation/TemplateSelector.vue";
import { faWebhook } from "@fortawesome/pro-solid-svg-icons";

const api = useApi();
const { user } = useUser();
const props = defineProps<{
    type?: string;
    app?: string;
    tags?: string[];
    templates?: Template[];
    hideTabs?: boolean;
    hideTitle?: boolean;
    wantsPreview?: boolean;
    search?: string;
}>();

const emit = defineEmits<{
    (e: "select", template: Template): void;
}>();

const searchTerm = ref(props.search || "");

const query = computed(() => {
    return {
        type: props.type,
        app: props.app,
        tags: props.tags,
    };
});

const { data: response, items } = api.templates.list({
    query,
});

const selected = defineModel<Template | undefined>("selected", {
    default: undefined,
});

const STEP_TYPE_ICONS: Record<string, [IconDefinition, string]> = {
    trigger: [faBolt, ""],
    scheduled: [faClock, ""],
    filter: [faFilters, ""],
    delay: [faClockRotateLeft, ""],
    send_survey: [faSquarePollVertical, ""],
    send_email: [faEnvelopes, ""],
    start_conversation: [faMessages, ""],
    update_field: [faPenToSquare, ""],
    log_activity: [faFilePlus, ""],
    schedule_task: [faListCheck, ""],
    send_postcard: [faAddressCard, ""],
    schedule_post: [faCalendarPen, ""],
    find_email: [faMagnifyingGlass, ""],
    post_webhook: [faWebhook, ""],
    verify_email: [faBadgeCheck, ""],
    search_domain: [faGlobePointer, ""],
    sync_person: [faArrowsRotate, ""],
    send_message: [faMessages, ""],
};

const template_items = computed(() => {
    let givenTemplates = props.templates ?? [];
    const loadedTemplates = (items.value ?? []) as unknown as Template[];
    const hasFeatured = loadedTemplates.some(
        (template) => template.is_featured,
    );

    if (!hasFeatured && givenTemplates.length) {
        givenTemplates = givenTemplates.map((template) => {
            return {
                ...template,
                is_featured: false,
            };
        });
    }

    return [...givenTemplates, ...loadedTemplates];
});

function getTemplateItemsFor(app_or_feature: string) {
    return template_items.value.filter((item: Template) => {
        if (item.show_anyways) {
            return true;
        }

        if (app_or_feature === "home") {
            return item.is_featured;
        } else if (app_or_feature === "all") {
            return true;
        }

        return (
            item.app?.value === app_or_feature ||
            (item.metadata &&
                item.metadata.steps &&
                item.metadata.steps.filter(
                    (step: AutomationTemplateMetadataStep) => {
                        return step.app.value === app_or_feature;
                    },
                ).length)
        );
    });
}

const filteredTemplates = computed<TemplateObject<Template>[]>(() => {
    let templates = getTemplateItemsFor(app_or_feature.value);

    if (searchTerm.value) {
        const search = searchTerm.value.toLowerCase();
        templates = templates.filter((item: Template) => {
            return item.title?.toLowerCase().includes(search);
        });
    }

    return templates.map((item: Template): TemplateObject<Template> => {
        return {
            isButton: true,
            label: item.title,
            description: item.description,
            badges: item.tags,
            object: item,
        } as TemplateObject<Template>;
    });
});

const app_or_feature = ref<string>(
    props.app ??
        (useRoute().query.tab as string) ??
        user.value?.account?.core_app?.value ??
        "home",
);

const app_and_features = computed(() => {
    const items =
        user.value?.account?.core_app?.value ||
        getTemplateItemsFor("home").length === 0
            ? []
            : [
                  {
                      label: "Home",
                      icon: faHome,
                      value: "home",
                      title: "Featured Templates",
                      active: app_or_feature.value === "home",
                  },
              ];

    if (
        response.value?.extra?.apps &&
        Array.isArray(response.value?.extra?.apps)
    ) {
        for (const app of response.value.extra.apps) {
            items.push({
                label: app.label,
                icon: app.icon,
                value: app.value,
                title: `${app.label} Templates`,
                active: app_or_feature.value === app.value,
            });
        }
    }

    items.push({
        label: "All",
        icon: faBars,
        value: "all",
        title: "All Templates",
        active: app_or_feature.value === "all",
    });

    if (user.value?.account?.core_app?.value) {
        return useSortBy(
            items,
            (item) => item.value !== user.value?.account?.core_app?.value,
        );
    }

    return items;
});

const selected_app_and_feature = computed(() =>
    app_and_features.value.find((item) => item.value === app_or_feature.value),
);

watchImmediate(response, () => {
    if (props.app || !template_items.value.length) {
        return;
    }

    if (
        app_or_feature.value === "home" &&
        getTemplateItemsFor(app_or_feature.value).length === 0
    ) {
        app_or_feature.value = "all";
    } else if (
        !["home", "all"].includes(app_or_feature.value) &&
        getTemplateItemsFor(app_or_feature.value).length === 0
    ) {
        app_or_feature.value = "all";
    }
});

const isAllOnly = computed(
    () =>
        app_or_feature.value != "all" &&
        template_items.value.length === filteredTemplates.value.length,
);

const previewing = ref<boolean>(false);
function onSelect(template: Template, final: boolean = false) {
    selected.value = template;

    if (props.wantsPreview && !template.is_blank && !final) {
        previewing.value = true;
        return;
    }

    previewing.value = false;
    emit("select", template);
}

const locale = computed(() => {
    return useUser().user.value?.account?.locale ?? null;
});

// Watch for changes to the search prop
watch(
    () => props.search,
    (newValue: string | undefined) => {
        if (newValue !== undefined) {
            searchTerm.value = newValue;
        }
    },
    { immediate: true },
);
</script>

<template>
    <div class="flex flex-col gap-4">
        <div
            v-if="!hideTabs && !isAllOnly && app_and_features.length > 1"
            class="flex gap-4 justify-between"
        >
            <div class="flex gap-2">
                <UButton
                    v-for="app_and_feature in app_and_features"
                    :key="app_and_feature.value"
                    :label="app_and_feature.label"
                    :color="app_and_feature.active ? 'hd-secondary' : 'gray'"
                    variant="soft"
                    @click="app_or_feature = app_and_feature.value"
                >
                    <template #leading>
                        <nuxt-img
                            v-if="typeof app_and_feature.icon === 'string'"
                            class="h-6 w-6 object-contain"
                            :src="app_and_feature.icon"
                            :alt="app_and_feature.label"
                        />
                        <FontAwesomeIcon
                            v-else-if="app_and_feature.icon"
                            :icon="app_and_feature.icon"
                        />
                    </template>
                </UButton>
            </div>

            <div>
                <UInput
                    v-model="searchTerm"
                    class="w-80"
                    placeholder="Search Templates"
                    size="lg"
                />
            </div>
        </div>
        <h2
            v-if="
                selected_app_and_feature?.title &&
                selected_app_and_feature?.title !== false &&
                !hideTitle
            "
            class="font-medium text-lg"
        >
            {{
                selected_app_and_feature.title ?? selected_app_and_feature.label
            }}
        </h2>
        <div class="grid grid-cols-2 3xl:grid-cols-4 gap-4">
            <AutomationTemplateSelectorItem
                v-for="template in filteredTemplates"
                :key="template.id"
                :template="template"
                @select="onSelect(template.object)"
                class="items-start justify-start"
            >
                <template #pre-badges>
                    <nuxt-img
                        :src="template.object.app.icon"
                        class="h-6 w-6 object-cover"
                    />
                </template>
                <template #post-cta>
                    <div class="mt-auto pt-2">
                        <div class="flex rounded-md gap-1 items-center">
                            <template
                                v-for="(step, index) in template.object.metadata
                                    .steps"
                                :key="index"
                            >
                                <FontAwesomeIcon
                                    v-if="index > 0"
                                    :icon="faChevronRight"
                                />
                                <UTooltip
                                    class="w-8 h-8 flex items-center justify-center"
                                    :text="
                                        step.action
                                            ? `${step.app.label} > ${step.action.label}`
                                            : `${step.type.label}`
                                    "
                                >
                                    <FontAwesomeIcon
                                        v-if="
                                            step?.type?.value &&
                                            STEP_TYPE_ICONS[step.type.value]
                                        "
                                        size="lg"
                                        :icon="
                                            STEP_TYPE_ICONS[step.type.value][0]
                                        "
                                        :class="
                                            STEP_TYPE_ICONS[step.type.value][1]
                                        "
                                    />
                                    <FontAwesomeIcon
                                        v-else-if="
                                            step?.action?.value &&
                                            STEP_TYPE_ICONS[step.action.value]
                                        "
                                        size="lg"
                                        :icon="
                                            STEP_TYPE_ICONS[
                                                step.action.value
                                            ][0]
                                        "
                                        :class="
                                            STEP_TYPE_ICONS[
                                                step.action.value
                                            ][1]
                                        "
                                    />
                                    <nuxt-img
                                        v-else
                                        :src="step.app.icon"
                                        class="h-6 w-6 object-cover"
                                    />
                                </UTooltip>
                            </template>
                        </div>
                    </div>
                </template>
                <template #post-label>
                    <nuxt-img
                        v-if="template.object.banner"
                        :src="template.object.banner"
                        class="w-full rounded-xl mt-2"
                    />
                    <UTooltip
                        v-if="template.object.metadata.has_ai"
                        text="Powered by AI"
                        class="absolute text-primary-500 right-4 top-4"
                        :popper="{ placement: 'top' }"
                    >
                        <FontAwesomeIcon :icon="faSparkles" size="xl" />
                    </UTooltip>
                </template>
            </AutomationTemplateSelectorItem>
        </div>
        <AppPreviewModal
            v-if="previewing && selected"
            v-model:show="previewing"
            :src="selected.urls.preview"
            :name="selected.title"
            default-view="tablet"
            :default-locale="locale"
            :language-props="{
                size: 'md',
                allowedLocales: selected.locales,
                allowsEmpty: false,
                noneLabel: 'Default',
                defaultLocaleCode: locale?.code ?? selected.locales?.[0],
                hideIfSingle: true,
            }"
        >
            <template #footer>
                <div class="flex justify-end items-center gap-2">
                    <UButton
                        size="lg"
                        color="white"
                        @click="previewing = false"
                    >
                        Cancel
                    </UButton>
                    <UButton size="lg" @click="onSelect(selected, true)">
                        <FontAwesomeIcon />
                        Use Template
                    </UButton>
                </div>
            </template>
        </AppPreviewModal>
    </div>
</template>
